import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  PhoneCall, Youtube, Send, Facebook, Instagram, Twitter, Mail,
  MessagesSquare, Globe, AtSign, Loader2, ExternalLink, ThumbsUp,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

function styleFor(platform) {
  const p = platform.toLowerCase();
  if (p.includes("reddit")) return { letter: "r/", gold: true, action: "FOLLOW" };
  if (p.includes("discord")) return { Icon: MessagesSquare, gold: true, action: "JOIN" };
  if (p.includes("youtube")) return { Icon: Youtube, row: "bg-red-50 border-red-200 hover:border-red-300", chip: "bg-red-600", action: "SUBSCRIBE", actionCls: "bg-red-600" };
  if (p.includes("facebook")) return { Icon: ThumbsUp, row: "bg-blue-50 border-blue-200 hover:border-blue-300", chip: "bg-blue-600", action: "FOLLOW", actionCls: "bg-blue-600" };
  if (p.includes("website")) return { Icon: Globe, row: "bg-emerald-50 border-emerald-200 hover:border-emerald-300", chip: "bg-emerald-600", action: "VISIT NOW", actionCls: "bg-emerald-600" };
  if (p.includes("instagram")) return { Icon: Instagram, row: "bg-pink-50 border-pink-200 hover:border-pink-300", chip: "bg-pink-600", action: "FOLLOW", actionCls: "bg-pink-600" };
  if (p.includes("threads")) return { Icon: AtSign, row: "bg-slate-100 border-slate-300 hover:border-slate-400", chip: "bg-slate-800", action: "FOLLOW", actionCls: "bg-slate-800" };
  if (p === "x" || p.startsWith("x ") || p.includes("twitter")) return { Icon: Twitter, row: "bg-sky-50 border-sky-200 hover:border-sky-300", chip: "bg-slate-900", action: "FOLLOW", actionCls: "bg-slate-900" };
  if (p.includes("bluesky")) return { Icon: Globe, row: "bg-sky-100 border-sky-200 hover:border-sky-300", chip: "bg-sky-500", action: "FOLLOW", actionCls: "bg-sky-500" };
  if (p.includes("mastodon")) return { letter: "m", row: "bg-violet-50 border-violet-200 hover:border-violet-300", chip: "bg-violet-600", action: "FOLLOW", actionCls: "bg-violet-600" };
  if (p.includes("whatsapp")) return { Icon: PhoneCall, row: "bg-emerald-100/70 border-emerald-200 hover:border-emerald-300", chip: "bg-emerald-600", action: "MESSAGE ONLY", actionCls: "bg-emerald-700" };
  if (p.includes("email") || p.includes("mail")) return { Icon: Mail, row: "bg-rose-50 border-rose-200 hover:border-rose-300", chip: "bg-rose-600", action: "SEND EMAIL", actionCls: "bg-rose-600" };
  if (p.includes("telegram")) return { Icon: Send, row: "bg-sky-50 border-sky-200", chip: "bg-sky-600", action: "JOIN", actionCls: "bg-sky-600" };
  return { Icon: Globe, row: "bg-slate-50 border-slate-200", chip: "bg-sky-700", action: "OPEN", actionCls: "bg-sky-700" };
}

export default function SocialsPage() {
  const [socials, setSocials] = useState(null);

  useEffect(() => {
    api.get("/socials").then((r) => setSocials(r.data)).catch(() => setSocials([]));
  }, []);

  return (
    <div data-testid="socials-page" className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-4xl px-6 pb-28 pt-32 sm:px-10">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-sky-700"
        >
          Stay Connected
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8, ease: EASE }}
          className="mt-4 font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl"
        >
          Jyoti Academy, everywhere you are.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
          className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
        >
          Join our online community for updates, learning resources, tips and more —
          search the username on your favourite app.
        </motion.p>

        <motion.figure
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.9, ease: EASE }}
          className="mx-auto mt-12 max-w-md overflow-hidden rounded-3xl border border-amber-200 bg-white shadow-xl shadow-amber-900/10"
          data-testid="socials-card-image"
        >
          <img
            src="/assets/socials-card.png"
            alt="Jyoti Academy — join our online community: YouTube, Facebook, Website, Instagram, Threads, X, Bluesky, Mastodon, WhatsApp and Email"
            className="w-full object-cover"
          />
        </motion.figure>

        {socials === null ? (
          <div className="mt-16 flex items-center gap-3 text-slate-500" data-testid="socials-loading">
            <Loader2 size={18} className="animate-spin" /> Loading…
          </div>
        ) : socials.length === 0 ? (
          <p className="mt-16 rounded-3xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="socials-empty">
            Social links will appear here once added by Jyoti Academy.
          </p>
        ) : (
          <div className="mt-14 space-y-3">
            {socials.map((s, i) => {
              const st = styleFor(s.platform);
              const Icon = st.Icon;
              const gold = st.gold;
              return (
                <motion.a
                  key={s.id}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.05, duration: 0.6, ease: EASE }}
                  whileHover={{ x: 6 }}
                  data-testid={`social-link-${i}`}
                  className={`group flex items-center gap-4 rounded-3xl border p-4 shadow-sm transition-colors duration-200 sm:p-5 ${
                    gold ? "border-slate-800 bg-slate-950 hover:border-amber-500/60" : st.row
                  }`}
                >
                  <span
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl font-mono text-base font-bold ${
                      gold ? "border border-amber-500/40 bg-amber-500/10 text-amber-400" : `text-white ${st.chip}`
                    }`}
                  >
                    {st.letter ? st.letter : Icon && <Icon size={20} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block font-display text-lg font-bold leading-tight ${gold ? "text-amber-400" : "text-slate-900"}`}>
                      {s.platform}
                    </span>
                    {s.handle && (
                      <span className={`mt-0.5 block truncate font-mono text-xs font-semibold sm:text-sm ${gold ? "text-amber-300" : "text-sky-700"}`} data-testid={`social-handle-${i}`}>
                        {s.handle}
                      </span>
                    )}
                    {s.badge && (
                      <span className={`mt-1 block text-[11px] font-semibold uppercase tracking-widest ${gold ? "text-amber-300/70" : "text-slate-500"}`}>
                        {s.badge}
                      </span>
                    )}
                  </span>
                  <span
                    className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white transition-transform duration-200 group-hover:scale-105 sm:px-5 sm:text-xs ${
                      gold ? "border border-amber-500/50 bg-amber-500/15 text-amber-300" : st.actionCls
                    }`}
                  >
                    {st.action}
                    <ExternalLink size={12} className="hidden sm:block" />
                  </span>
                </motion.a>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
